import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useOrders } from '../../contexts/OrdersContext';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import DeliveryMap from '../../components/feature/DeliveryMap';
import { formatEtaRange } from '../../utils/routeUtils';
import type { AdminOrder, AdminOrderStatus } from '../../mocks/adminOrders';

type StatusKey = AdminOrderStatus | 'payment_pending';

const STEPS = [
  { label: 'Order Received', sub: 'Your order was submitted', icon: 'ri-file-list-3-line', key: 'received' },
  { label: 'Payment Confirmed', sub: 'Payment approved by our team', icon: 'ri-shield-check-line', key: 'confirmed' },
  { label: 'Preparing Order', sub: 'Being packed and prepared', icon: 'ri-store-2-line', key: 'preparing' },
  { label: 'Out for Delivery', sub: 'Rider is on the way to you', icon: 'ri-motorbike-line', key: 'on_way' },
  { label: 'Delivered', sub: 'Enjoy your beverages!', icon: 'ri-checkbox-circle-line', key: 'delivered' },
];

function getActiveStep(status: StatusKey): number {
  switch (status) {
    case 'payment_pending': return 0;
    case 'pending': return 1;
    case 'processing': return 2;
    case 'on_way': return 3;
    case 'delivered': return 4;
    default: return 0;
  }
}

function getStatusMeta(status: StatusKey): { label: string; color: string; bg: string; icon: string } {
  switch (status) {
    case 'payment_pending':
      return { label: 'Awaiting Confirmation', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-300', icon: 'ri-time-line' };
    case 'pending':
      return { label: 'Payment Confirmed', color: 'text-green-700', bg: 'bg-green-50 border-green-300', icon: 'ri-shield-check-line' };
    case 'processing':
      return { label: 'Preparing Your Order', color: 'text-[#FF6B35]', bg: 'bg-orange-50 border-orange-300', icon: 'ri-store-2-line' };
    case 'on_way':
      return { label: 'Out for Delivery', color: 'text-indigo-700', bg: 'bg-indigo-50 border-indigo-200', icon: 'ri-motorbike-line' };
    case 'delivered':
      return { label: 'Delivered!', color: 'text-green-700', bg: 'bg-green-50 border-green-300', icon: 'ri-checkbox-circle-fill' };
    case 'cancelled':
      return { label: 'Cancelled', color: 'text-red-600', bg: 'bg-red-50 border-red-300', icon: 'ri-close-circle-line' };
    default:
      return { label: 'Processing', color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200', icon: 'ri-loader-4-line' };
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GY', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function OrderNotFound({ id }: { id: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center px-6">
      <div className="w-24 h-24 flex items-center justify-center rounded-full bg-gray-100 text-gray-300 mb-6">
        <i className="ri-search-line text-5xl" />
      </div>
      <h2 className="text-2xl font-black text-gray-800 mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>Order Not Found</h2>
      <p className="text-gray-400 text-sm max-w-sm mb-8">
        We couldn&apos;t find an order with ID <strong className="text-gray-600">{id}</strong>.
        Double-check the ID or use the tracking page to search manually.
      </p>
      <div className="flex gap-3">
        <Link to="/tracking" className="bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-bold px-6 py-3 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap">
          Track by ID
        </Link>
        <button onClick={() => navigate(-1)} className="border border-gray-200 text-gray-600 font-medium px-6 py-3 rounded-full text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
          Go Back
        </button>
      </div>
    </div>
  );
}

function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-xs font-semibold text-green-700">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
      Live
    </span>
  );
}

export default function OrderTrackingPage() {
  const { id } = useParams<{ id: string }>();
  const { orders } = useOrders();
  const [showMap, setShowMap] = useState(false);
  const [prevStatus, setPrevStatus] = useState<StatusKey | null>(null);
  const [justUpdated, setJustUpdated] = useState(false);

  const order: AdminOrder | undefined = orders.find((o) => o.id === (id ?? '').toUpperCase() || o.id === id);

  useEffect(() => {
    if (!order) return;
    const s = order.status as StatusKey;
    if (prevStatus !== null && prevStatus !== s) {
      setJustUpdated(true);
      const t = setTimeout(() => setJustUpdated(false), 5000);
      return () => clearTimeout(t);
    }
    setPrevStatus(s);
  }, [order?.status]);

  const isCancelled = order?.status === 'cancelled';
  const isDelivered = order?.status === 'delivered';
  const isPending = order?.status === 'payment_pending';
  const activeStep = order ? getActiveStep(order.status as StatusKey) : 0;
  const statusMeta = order ? getStatusMeta(order.status as StatusKey) : null;
  const etaRange = order ? formatEtaRange(order.address) : '20–35 min';

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex flex-col">
      <Navbar />

      {/* Page Header */}
      <div className="bg-[#0D0D0D] pt-24 pb-10 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <Link to="/account" className="text-white/50 hover:text-white/80 text-sm transition-colors cursor-pointer flex items-center gap-1">
              <i className="ri-arrow-left-line" />
              My Orders
            </Link>
            <i className="ri-arrow-right-s-line text-white/30 text-xs" />
            <span className="text-white/50 text-sm">{id}</span>
          </div>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-white font-black leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(32px, 5vw, 56px)', letterSpacing: '0.04em' }}>
                Order Tracking
              </h1>
              <p className="text-white/50 text-sm mt-1">Real-time status for order <strong className="text-white/70">{id}</strong></p>
            </div>
            {order && !isCancelled && !isDelivered && <LiveBadge />}
          </div>
        </div>
      </div>

      <main className="flex-1 py-10 px-6">
        <div className="max-w-4xl mx-auto">

          {/* Update banner */}
          {justUpdated && (
            <div className="mb-6 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-5 py-4">
              <div className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                <i className="ri-refresh-line text-green-600 text-lg" />
              </div>
              <div>
                <p className="text-sm font-bold text-green-800">Status updated!</p>
                <p className="text-xs text-green-600 mt-0.5">Your order status just changed. This page is always up to date.</p>
              </div>
            </div>
          )}

          {!order && id && <OrderNotFound id={id} />}

          {order && (
            <div className="grid lg:grid-cols-5 gap-6">
              {/* Left — Tracking */}
              <div className="lg:col-span-3 space-y-5">

                {/* Status Card */}
                <div className={`rounded-2xl border p-5 ${statusMeta?.bg}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Current Status</span>
                    {!isCancelled && !isDelivered && <LiveBadge />}
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-full ${isCancelled ? 'bg-red-100' : isDelivered ? 'bg-green-100' : 'bg-white/70'} flex-shrink-0`}>
                      <i className={`${statusMeta?.icon} text-2xl ${statusMeta?.color}`} />
                    </div>
                    <div>
                      <p className={`text-xl font-black ${statusMeta?.color}`} style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
                        {statusMeta?.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{formatDate(order.createdAt)}</p>
                    </div>
                  </div>
                  {isPending && (
                    <p className="text-xs text-amber-700 mt-3 bg-amber-100 rounded-lg px-3 py-2">
                      <i className="ri-information-line mr-1" />
                      Your order is awaiting payment confirmation from our team. You'll be notified once approved.
                    </p>
                  )}
                </div>

                {/* Progress Steps */}
                {!isCancelled ? (
                  <div className="bg-white border border-gray-100 rounded-2xl p-6">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-5">Order Journey</p>
                    <div className="space-y-0">
                      {STEPS.map((step, i) => {
                        const isDone = i < activeStep;
                        const isActive = i === activeStep;
                        return (
                          <div key={step.key} className="flex gap-4">
                            {/* Connector column */}
                            <div className="flex flex-col items-center">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all duration-500 ${
                                isDone
                                  ? 'bg-[#FF6B35] border-[#FF6B35]'
                                  : isActive
                                  ? 'bg-white border-[#FF6B35] scale-110'
                                  : 'bg-white border-gray-200'
                              }`}>
                                {isDone ? (
                                  <i className="ri-check-line text-white text-sm" />
                                ) : (
                                  <i className={`${step.icon} text-sm ${isActive ? 'text-[#FF6B35] animate-pulse' : 'text-gray-300'}`} />
                                )}
                              </div>
                              {i < STEPS.length - 1 && (
                                <div className={`w-0.5 flex-1 min-h-[28px] my-1 transition-all duration-500 ${i < activeStep ? 'bg-[#FF6B35]' : 'bg-gray-100'}`} />
                              )}
                            </div>
                            {/* Content */}
                            <div className="flex-1 pb-6">
                              <div className="flex items-center justify-between">
                                <p className={`text-sm font-bold transition-colors ${isDone || isActive ? 'text-gray-900' : 'text-gray-300'}`}>
                                  {step.label}
                                  {i === 3 && order.rider && (isDone || isActive) && (
                                    <span className="ml-2 text-xs font-normal text-[#FF6B35]">· {order.rider}</span>
                                  )}
                                </p>
                                {isActive && (
                                  <span className="flex items-center gap-1 text-xs text-[#FF6B35] font-semibold">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B35] animate-pulse" />
                                    In progress
                                  </span>
                                )}
                                {isDone && (
                                  <i className="ri-check-double-line text-[#FF6B35] text-sm" />
                                )}
                              </div>
                              <p className={`text-xs mt-0.5 ${isActive ? 'text-[#FF6B35]' : isDone ? 'text-gray-400' : 'text-gray-200'}`}>
                                {step.sub}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0">
                      <i className="ri-close-circle-line text-red-500 text-2xl" />
                    </div>
                    <div>
                      <p className="text-base font-bold text-red-700 mb-1">This order was cancelled</p>
                      <p className="text-sm text-red-500">If you have questions or need assistance, please contact our support team via WhatsApp.</p>
                      <p className="text-xs text-red-400 mt-2">Placed on {formatDate(order.createdAt)}</p>
                    </div>
                  </div>
                )}

                {/* Rider Card */}
                {order.rider && !isCancelled && (
                  <div className="bg-white border border-gray-100 rounded-2xl p-5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">Your Rider</p>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#e85d2a] flex items-center justify-center text-white font-black text-xl flex-shrink-0">
                        {order.rider.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <p className="text-base font-bold text-gray-900">{order.rider}</p>
                        <p className="text-xs text-gray-400 mt-0.5">SipDash Verified Rider</p>
                        <div className="flex items-center gap-1 mt-1">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <i key={s} className="ri-star-fill text-amber-400 text-xs" />
                          ))}
                          <span className="text-xs text-gray-400 ml-1">5.0</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FFF5F0] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white transition-colors cursor-pointer">
                          <i className="ri-phone-line text-base" />
                        </button>
                        <button className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FFF5F0] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white transition-colors cursor-pointer">
                          <i className="ri-message-2-line text-base" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Map */}
                {!isCancelled && (
                  <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                    <div className="px-5 py-4 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Delivery Route</p>
                        <p className="text-sm text-gray-600 mt-0.5 flex items-center gap-1">
                          <i className="ri-map-pin-line text-[#FF6B35]" />
                          {order.address}
                        </p>
                      </div>
                      <span className="text-xs font-bold text-[#FF6B35] bg-[#FFF5F0] border border-orange-100 px-3 py-1.5 rounded-full whitespace-nowrap">
                        ETA: {etaRange}
                      </span>
                    </div>
                    <div className="flex border-t border-gray-100">
                      <button
                        onClick={() => setShowMap(false)}
                        className={`flex-1 py-2 text-xs font-semibold transition-colors cursor-pointer ${!showMap ? 'text-[#FF6B35] border-b-2 border-[#FF6B35]' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        Status View
                      </button>
                      <button
                        onClick={() => setShowMap(true)}
                        className={`flex-1 py-2 text-xs font-semibold transition-colors cursor-pointer ${showMap ? 'text-[#FF6B35] border-b-2 border-[#FF6B35]' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        <i className="ri-map-2-line mr-1" />Map View
                      </button>
                    </div>
                    {showMap && (
                      <div className="h-64">
                        <DeliveryMap destination={order.address} />
                      </div>
                    )}
                    {!showMap && (
                      <div className="px-5 pb-5 pt-3 grid grid-cols-3 gap-3">
                        {[
                          { icon: 'ri-store-2-line', label: 'Origin', value: 'Georgetown Store' },
                          { icon: 'ri-arrow-right-line', label: '', value: '' },
                          { icon: 'ri-home-4-line', label: 'Destination', value: order.address.split(',')[0] },
                        ].map((item, i) => (
                          i === 1 ? (
                            <div key={i} className="flex items-center justify-center text-gray-300">
                              <div className="w-5 h-5 flex items-center justify-center">
                                <i className="ri-arrow-right-line text-lg" />
                              </div>
                            </div>
                          ) : (
                            <div key={i} className={`flex flex-col items-center gap-1 p-3 rounded-xl ${i === 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full ${i === 0 ? 'bg-orange-100 text-[#FF6B35]' : 'bg-green-100 text-green-600'}`}>
                                <i className={`${item.icon} text-base`} />
                              </div>
                              <p className="text-xs text-gray-400">{item.label}</p>
                              <p className="text-xs font-semibold text-gray-700 text-center leading-tight">{item.value}</p>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right — Order Details */}
              <div className="lg:col-span-2 space-y-5">
                {/* Order Info */}
                <div className="bg-white border border-gray-100 rounded-2xl p-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">Order Details</p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Order ID</span>
                      <span className="font-bold text-gray-900">{order.id}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Placed</span>
                      <span className="font-medium text-gray-700">{new Date(order.createdAt).toLocaleTimeString('en-GY', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Payment</span>
                      <span className="font-medium text-gray-700">{order.paymentMethod}</span>
                    </div>
                    {order.note && (
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">Note</p>
                        <p className="text-sm text-gray-600 italic">{order.note}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Items */}
                <div className="bg-white border border-gray-100 rounded-2xl p-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">Items Ordered</p>
                  <div className="space-y-3">
                    {order.items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#FFF5F0] text-[#FF6B35] flex-shrink-0">
                            <i className="ri-beer-line text-sm" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{item.name}</p>
                            <p className="text-xs text-gray-400">×{item.qty}</p>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-gray-700">GYD {item.price.toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="border-t border-gray-100 pt-3 flex justify-between">
                      <span className="text-sm font-bold text-gray-900">Total</span>
                      <span className="text-base font-black text-[#FF6B35]">GYD ${order.total.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Change info */}
                  {order.paymentMethod.toLowerCase().includes('cash') && order.changeAmount !== undefined && order.changeAmount >= 0 && (
                    <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-coins-line text-amber-600 text-sm" />
                        <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">Your Change</p>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="space-y-0.5">
                          <p className="text-xs text-amber-700">Cash: <strong>GYD ${(order.cashAmount ?? 0).toLocaleString()}</strong></p>
                          <p className="text-xs text-amber-700">Total: <strong>GYD ${order.total.toLocaleString()}</strong></p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-amber-600 font-medium">Rider brings</p>
                          <p className="text-xl font-black text-amber-700">GYD ${order.changeAmount.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delivery Address */}
                <div className="bg-white border border-gray-100 rounded-2xl p-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Delivery Address</p>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-50 text-red-400 flex-shrink-0 mt-0.5">
                      <i className="ri-map-pin-2-fill text-base" />
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{order.address}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  <Link
                    to="/menu"
                    className="w-full flex items-center justify-center gap-2 bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-bold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-beer-line" />
                    Order Again
                  </Link>
                  <Link
                    to="/account"
                    className="w-full flex items-center justify-center gap-2 border border-gray-200 text-gray-600 font-medium py-3 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-history-line" />
                    View All Orders
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
